import type { LiveWorkerLocationDto } from '../../../types/access'

// Pure, framework-free helpers for the Admin Live Map worker inclusion/status contract.
// Extracted from BreadcrumbTrailViewer so the operational-status rules can be unit tested.

export type WorkerMapStatus = 'punched_in' | 'break' | 'late' | 'missed_punch_out' | 'shift_ended' | 'other'
export type LocationSourceKey = 'live' | 'breadcrumb' | 'location_fallback'

export function normalizedStatus(value: unknown) {
  return String(value || '').trim().toLowerCase()
}

export function firstText(...values: Array<unknown>) {
  const value = values.find((entry) => entry !== null && entry !== undefined && String(entry).trim() !== '')
  return value === undefined ? '' : String(value)
}

export function validMapPoint(lat?: number | null, lon?: number | null) {
  return typeof lat === 'number'
    && typeof lon === 'number'
    && Number.isFinite(lat)
    && Number.isFinite(lon)
    && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180
}

// Late = shift start has passed and the worker is not currently punched in or on break.
// The backend now authoritatively excludes excused (approved absence / unavailability) and
// completed/punched-out shifts from `isLate`, so the client trusts those flags when present.
export function workerIsLate(worker: LiveWorkerLocationDto) {
  const raw = worker as LiveWorkerLocationDto & Record<string, unknown>
  const status = normalizedStatus(worker.status)
  if (status === 'punched_in' || status === 'break') return false
  if (raw.isLate === true || raw.late === true || raw.timeState === 'late' || raw.scheduleStatus === 'late') return true
  if (raw.isLate === false || raw.late === false) return false
  if (status.includes('late')) return true
  const shiftStart = firstText(worker.shiftStartedAt, worker.shift_started_at, worker.startedAt, worker.started_at)
  if (!shiftStart) return false
  const parsed = Date.parse(shiftStart)
  return Number.isFinite(parsed) && parsed < Date.now()
}

// A worker is still actively punched in / on break when the backend says so.
export function workerIsActive(worker: LiveWorkerLocationDto) {
  const status = normalizedStatus(worker.status)
  return status === 'punched_in' || status === 'break'
}

// True when the worker still has an OPEN execution session past the scheduled
// job_shift end. Scheduled end NEVER hides the open session (BUG 1) — the
// backend surfaces scheduled_shift_ended / missed_punch_out / operational_state.
export function workerScheduledShiftEnded(worker: LiveWorkerLocationDto) {
  const raw = worker as LiveWorkerLocationDto & Record<string, unknown>
  if (!workerIsActive(worker)) return false
  if (raw.scheduledShiftEnded === true || raw.scheduled_shift_ended === true) return true
  const opState = normalizedStatus(raw.operationalState || raw.operational_state)
  return opState === 'missed_punch_out' || opState === 'punched_in_shift_ended' || opState === 'on_break_shift_ended'
}

export function workerMissedPunchOut(worker: LiveWorkerLocationDto) {
  const raw = worker as LiveWorkerLocationDto & Record<string, unknown>
  if (!workerIsActive(worker)) return false
  if (raw.missedPunchOut === true || raw.missed_punch_out === true) return true
  return normalizedStatus(raw.operationalState || raw.operational_state) === 'missed_punch_out'
}

export function workerMapStatus(worker: LiveWorkerLocationDto): WorkerMapStatus {
  const status = normalizedStatus(worker.status)
  if (workerMissedPunchOut(worker)) return 'missed_punch_out'
  if (workerScheduledShiftEnded(worker)) return 'shift_ended'
  if (status === 'break') return 'break'
  if (workerIsLate(worker)) return 'late'
  if (status === 'punched_in') return 'punched_in'
  return 'other'
}

// Human-readable operational label for live/location views (BUG 1 requirement C).
export function workerStatusLabel(worker: LiveWorkerLocationDto): string {
  const status = normalizedStatus(worker.status)
  const onBreak = status === 'break'
  if (workerMissedPunchOut(worker)) return 'Missed Punch Out'
  if (workerScheduledShiftEnded(worker)) return onBreak ? 'On Break — Shift Ended' : 'Punched In — Shift Ended'
  if (onBreak) return 'On Break'
  if (workerIsLate(worker)) return 'Late'
  if (status === 'punched_in') return 'Punched In'
  return 'Punched Out'
}

export function locationSourceKey(worker: LiveWorkerLocationDto): LocationSourceKey {
  const raw = normalizedStatus(worker.locationSource || worker.location_source)
  if (raw === 'location_fallback' || raw === 'breadcrumb' || raw === 'live') return raw
  if (worker.locationFallback) return 'location_fallback'
  const source = normalizedStatus(worker.source)
  if (source.includes('breadcrumb') || source.includes('tracking')) return 'breadcrumb'
  return 'live'
}

// A fallback location is the assigned-location center standing in for a missing device fix.
// Such coordinates must never be presented as the worker's real position.
export function isFallbackLocation(worker: LiveWorkerLocationDto) {
  return locationSourceKey(worker) === 'location_fallback' || Boolean(worker.locationFallback)
}

export interface WorkerMarkerStyle {
  color: string
  fillColor: string
  fillOpacity: number
  dashArray?: string
  locationFallback: boolean
}

// Preserve the green/orange/red status contract for the marker fill even when the worker has
// no live fix. Fallback positions keep the status color but render dashed + semi-transparent so
// they read as approximate rather than a real location.
export function workerStatusMarkerColors(worker: LiveWorkerLocationDto): WorkerMarkerStyle {
  const mapStatus = workerMapStatus(worker)
  const base = mapStatus === 'missed_punch_out'
    ? { color: '#b91c1c', fillColor: '#ef4444' }
    : mapStatus === 'shift_ended'
      ? { color: '#c2410c', fillColor: '#fb923c' }
      : mapStatus === 'late'
        ? { color: '#b91c1c', fillColor: '#ef4444' }
        : mapStatus === 'break'
          ? { color: '#b45309', fillColor: '#f59e0b' }
          : mapStatus === 'punched_in'
            ? { color: '#15803d', fillColor: '#22c55e' }
            : worker.isStale
              ? { color: '#6b7280', fillColor: '#9ca3af' }
              : { color: '#334155', fillColor: '#64748b' }
  const usesFallback = isFallbackLocation(worker)
  return {
    ...base,
    fillOpacity: usesFallback ? 0.45 : 0.9,
    dashArray: usesFallback ? '4 3' : undefined,
    locationFallback: usesFallback,
  }
}

// CSS-friendly marker class used for legend/marker parity and for tests.
export function workerStatusMarkerClass(worker: LiveWorkerLocationDto) {
  return `live-worker-marker live-worker-marker--${workerMapStatus(worker)}${isFallbackLocation(worker) ? ' live-worker-marker--fallback' : ''}`
}

export interface LiveWorkerCounts {
  total: number
  punchedIn: number
  onBreak: number
  late: number
  missedPunchOut: number
  shiftEnded: number
  other: number
  withGps: number
  fallback: number
  stale: number
}

export function countLiveWorkers(workers: LiveWorkerLocationDto[]): LiveWorkerCounts {
  return workers.reduce<LiveWorkerCounts>((acc, worker) => {
    const status = workerMapStatus(worker)
    acc.total += 1
    // Shift-ended / missed-punch-out workers are still actively punched in — they
    // count toward punchedIn/onBreak AND toward their own operational bucket so
    // supervisors see the open sessions needing attention.
    if (status === 'missed_punch_out' || status === 'shift_ended') {
      acc.shiftEnded += 1
      if (status === 'missed_punch_out') acc.missedPunchOut += 1
      if (normalizedStatus(worker.status) === 'break') acc.onBreak += 1
      else acc.punchedIn += 1
    } else if (status === 'punched_in') acc.punchedIn += 1
    else if (status === 'break') acc.onBreak += 1
    else if (status === 'late') acc.late += 1
    else acc.other += 1
    if (validMapPoint(worker.lat, worker.lon) && !isFallbackLocation(worker) && !worker.missingGps) acc.withGps += 1
    if (isFallbackLocation(worker)) acc.fallback += 1
    if (worker.isStale) acc.stale += 1
    return acc
  }, { total: 0, punchedIn: 0, onBreak: 0, late: 0, missedPunchOut: 0, shiftEnded: 0, other: 0, withGps: 0, fallback: 0, stale: 0 })
}
